import { useCallback, useMemo } from 'react';
import { UseAuthenticationHook } from '../contexts';
import { useUserTags } from './useLocalTags';
import {
  tagContextMatches as tagContextMatchesBase,
  type ITagContextMatchesParams,
} from '../utils';

const createUseTagContext =
  (useAuthentication: UseAuthenticationHook, isAppNative: boolean) =>
  ({ isWebMobile }: { isWebMobile?: boolean } = { isWebMobile: false }) => {
    const { isAuthenticated, customer, isMember } = useAuthentication();
    const localTags = useUserTags(useAuthentication);

    return useMemo(
      () => ({
        tags: {
          user: localTags,
        },
        isAuthenticated,
        orders: customer?.orderCount,
        isMember,
        isWebMobile,
        isAppNative,
      }),
      [localTags, isAuthenticated, customer?.orderCount, isMember, isWebMobile],
    );
  };

/**
 * Factory function that build a useTagContextMatches hook
 * This hook is used to retrieve a function that assert 'condition' fields agains current auth user context
 *
 * @param useAuthentication the hook that handles the authentication
 * @returns a useTagContextMatches hook
 */
export const createUseTagContextMatches = (
  useAuthentication: UseAuthenticationHook,
  isAppNative: boolean,
) => {
  const useTagContext = createUseTagContext(useAuthentication, isAppNative);

  const useTagContextMatches = (
    { isWebMobile }: { isWebMobile?: boolean } = { isWebMobile: false },
  ) => {
    const tagContext = useTagContext({ isWebMobile });

    const tagContextMatches = useCallback(
      (
        expression: string,
        { tags, ...params }: ITagContextMatchesParams | undefined = {
          tags: {},
        },
      ) =>
        tagContextMatchesBase(expression, {
          ...tagContext,
          ...params,
          tags: {
            ...tagContext.tags,
            ...tags,
            user: [...tagContext.tags.user, ...(tags?.user || [])],
          },
        }),
      [tagContext],
    );

    return {
      tagContextMatches,
    };
  };

  return { useTagContext, useTagContextMatches };
};
